import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import { IconWrapper } from '../common/IconWrapper';
import {
  HiXMark,
  HiBars3,
  HiChevronDown
} from 'react-icons/hi2';

type NavSubLink = {
  name: string;
  path: string;
};

type MegaMenuConfig = {
  services: {
    title: string;
    viewAllPath: string;
    categories: {
      title: string;
      items: NavSubLink[];
    }[];
  };
  solutions: {
    title: string;
    viewAllPath: string;
    cards: {
      name: string;
      description: string;
      path: string;
      gradient: string;
    }[];
  };
};

type NavLinkItem = {
  name: string;
  path: string;
  subLinks?: NavSubLink[];
  megaMenu?: MegaMenuConfig;
};

const navLinks: NavLinkItem[] = [
  { name: 'Home', path: '/' },
  { 
    name: 'Services & Solutions', 
    path: '/services',
    megaMenu: {
      services: {
        title: 'Services ↗',
        viewAllPath: '/services',
        categories: [
          {
            title: 'Agentic AI',
            items: [
              { name: 'Azure Foundry', path: '/services/azure-foundry' },
              { name: 'Azure Open AI', path: '/services/azure-open-ai' },
            ],
          },
          {
            title: 'Data Engineering & Analytics',
            items: [
              { name: 'Datalake/Lakeview/Data Warehouse', path: '/services/data-warehouse' },
              { name: 'ETL (Azure Data Factory/Databricks)', path: '/services/etl' },
              { name: 'BI Reporting (Power BI)', path: '/services/bi-reporting' },
            ],
          },
          {
            title: 'Application Engineering',
            items: [
              { name: 'Application Development', path: '/services/application-development' },
              { name: 'Application Modernization', path: '/services/application-modernization' },
            ],
          },
          {
            title: 'Cloud Data Services',
            items: [
              { name: 'Azure', path: '/services/cloud-azure' },
              { name: 'GCP', path: '/services/cloud-gcp' },
              { name: 'AWS', path: '/services/cloud-aws' },
            ],
          },
          {
            title: 'DevOps',
            items: [
              { name: 'CI/CD Pipelines', path: '/services/cicd-pipelines' },
            ],
          },
        ],
      },
      solutions: {
        title: 'Solutions ↗',
        viewAllPath: '/solutions',
        cards: [
          {
            name: 'Retail & E-commerce',
            description: 'Personalize journeys and optimize omnichannel ops.',
            path: '/solutions/retail-ecommerce',
            gradient: 'from-blue-500 to-cyan-600',
          },
          {
            name: 'Manufacturing & Automotive',
            description: 'Enable smart factories and connected product insights.',
            path: '/solutions/manufacturing',
            gradient: 'from-indigo-500 to-purple-600',
          },
          {
            name: 'Education',
            description: 'Transform learning experiences with digital platforms and analytics.',
            path: '/solutions/education',
            gradient: 'from-blue-500 to-slate-700',
          },
        ],
      },
    }
  },
  { name: 'Case Studies', path: '/case-studies' },
  { name: 'Careers', path: '/careers' },
  { name: 'Contact', path: '/contact' },
];

export const Navbar: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [openMobileDropdown, setOpenMobileDropdown] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMobileMenuClose = () => {
    setIsMobileMenuOpen(false);
    setOpenMobileDropdown(null);
  };

  const navbarClasses = `
    fixed w-full z-50 transition-all duration-500 ease-in-out
    ${scrolled 
      ? 'py-3 bg-white/90 dark:bg-slate-900/90 shadow-xl backdrop-blur-xl border-b border-white/20 dark:border-slate-700/30' 
      : 'py-4 bg-transparent'
    }
  `;

  const linkColorClasses = scrolled
    ? 'text-slate-800 dark:text-slate-200 hover:text-blue-600 dark:hover:text-blue-400'
    : 'text-white hover:text-blue-200';

  const linkClasses = `
    relative px-5 py-2 text-sm font-semibold transition-all duration-300
    ${linkColorClasses}
    hover:scale-105 transform
  `;

  const activeLinkClasses = scrolled
    ? 'text-blue-600 dark:text-blue-400'
    : 'text-blue-200';

  return (
    <motion.nav
      className={navbarClasses}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ type: 'spring', stiffness: 120, damping: 14, delay: 0.2 }}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 lg:px-8">
        <motion.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Link to="/" className="flex items-center group">
            <div className="relative">
              <div className="w-32 h-14 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300 overflow-hidden bg-white border border-gray-200 px-2">
                <img 
                  src="/logo.png" 
                  alt="FirstLogic Technologies" 
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </Link>
        </motion.div>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center space-x-1 mr-16">
          {navLinks.map((link, index) => (
            link.megaMenu ? (
              <motion.div 
                key={link.name} 
                className="relative group"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                onMouseEnter={() => setIsMegaMenuOpen(true)}
                onMouseLeave={() => setIsMegaMenuOpen(false)}
              >
                <button
                  type="button"
                  className={`
                    ${linkClasses} flex items-center rounded-lg group-hover:bg-blue-50 dark:group-hover:bg-slate-800
                  `}
                >
                  {link.name}
                  <IconWrapper 
                    icon={HiChevronDown} 
                    className="ml-1 h-3 w-3 transition-transform group-hover:rotate-180" 
                  />
                </button>
                <div className={`absolute left-1/2 -translate-x-1/2 top-full mt-4 w-[1120px] rounded-3xl shadow-2xl p-6 bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border border-white/20 dark:border-slate-700/30 transition-all duration-300 transform origin-top scale-95 z-50 pl-6 ${isMegaMenuOpen ? 'opacity-100 visible scale-100' : 'opacity-0 invisible scale-95'}`}>
                  <div className="grid grid-cols-1 lg:grid-cols-[55%_45%] gap-6">
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-semibold uppercase tracking-wide text-blue-600">{link.megaMenu.services.title}</span>
                        <Link
                          to={link.megaMenu.services.viewAllPath}
                          onClick={() => setIsMegaMenuOpen(false)}
                          className="inline-flex items-center px-3 py-1 text-[11px] font-semibold text-blue-600 border border-blue-100 rounded-full bg-blue-50/60 hover:bg-blue-100 hover:text-blue-700 transition"
                        >
                          View all
                        </Link>
                      </div>
                      <div className="space-y-4">
                        {link.megaMenu.services.categories.map(category => (
                          <div key={category.title}>
                            <div className="flex items-center justify-between mb-1">
                              <h4 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">{category.title}</h4>
                              <div className="h-px flex-1 ml-2 bg-slate-200 dark:bg-slate-700" />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              {category.items.map(item => (
                                <NavLink
                                  key={item.name}
                                  to={item.path}
                                  onClick={() => setIsMegaMenuOpen(false)}
                                  className={({ isActive }) => `
                                    text-[12px] font-semibold rounded-lg px-3 py-1.5 transition-all duration-200 hover:bg-blue-50 dark:hover:bg-slate-800 hover:text-blue-600
                                    ${isActive ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-slate-800' : 'text-slate-700 dark:text-slate-300'}
                                  `}
                                >
                                  {item.name}
                                </NavLink>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="pr-2">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-semibold uppercase tracking-wide text-purple-600">{link.megaMenu.solutions.title}</span>
                        <Link
                          to={link.megaMenu.solutions.viewAllPath}
                          onClick={() => setIsMegaMenuOpen(false)}
                          className="inline-flex items-center px-3 py-1 text-[11px] font-semibold text-purple-600 border border-purple-100 rounded-full bg-purple-50/60 hover:bg-purple-100 hover:text-purple-700 transition"
                        >
                          View all
                        </Link>
                      </div>
                      <div className="grid grid-cols-1 gap-2.5">
                        {link.megaMenu.solutions.cards.map(card => (
                          <NavLink
                            key={card.name}
                            to={card.path}
                            onClick={() => setIsMegaMenuOpen(false)}
                            className={({ isActive }) => `
                              rounded-2xl p-3.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1
                              ${isActive ? 'ring-2 ring-blue-500' : ''}
                            `}
                          >
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">{card.name}</p>
                            <p className="text-sm font-semibold leading-snug mt-1.5 text-slate-900 dark:text-white">{card.description}</p>
                          </NavLink>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ) : link.subLinks ? (
              <motion.div 
                key={link.name} 
                className="relative group"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                onMouseEnter={() => {}}
                onMouseLeave={() => {}}
              >
                <button className={`${linkClasses} flex items-center group-hover:bg-blue-50 dark:group-hover:bg-slate-800 rounded-lg`}>
                  {link.name} 
                  <IconWrapper 
                    icon={HiChevronDown} 
                    className="ml-1 h-3 w-3 transition-transform group-hover:rotate-180" 
                  />
                </button>
                <div className="absolute left-0 top-full mt-2 w-64 rounded-2xl shadow-2xl p-3 bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border border-white/20 dark:border-slate-700/30 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform origin-top scale-95 group-hover:scale-100 z-50">
                  {link.subLinks.map((subLink, subIndex) => (
                    <NavLink
                      key={subLink.name}
                      to={subLink.path}
                      className={({ isActive }) => `
                        block px-4 py-3 text-sm rounded-xl transition-all duration-200
                        hover:bg-blue-50 dark:hover:bg-slate-800 hover:scale-105 transform
                        ${isActive ? 'text-blue-600 dark:text-blue-400 font-semibold bg-blue-50 dark:bg-slate-800' : 'text-slate-600 dark:text-slate-300'}
                      `}
                    >
                      {subLink.name}
                    </NavLink>
                  ))}
                </div>
              </motion.div>
            ) : (
              <motion.div
                key={link.name}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <NavLink
                  to={link.path}
                  className={({ isActive }) => `
                    ${linkClasses} rounded-lg hover:bg-blue-50 dark:hover:bg-slate-800
                    ${isActive ? activeLinkClasses : ''}
                  `}
                >
                  {link.name}
                </NavLink>
              </motion.div>
            )
          ))}
          
        </div>

        {/* Mobile Menu Button */}
        <div className="lg:hidden flex items-center space-x-3">
          <motion.button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-all duration-300"
            aria-label="Open mobile menu"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <IconWrapper 
              icon={isMobileMenuOpen ? HiXMark : HiBars3} 
              className="h-6 w-6 text-slate-700 dark:text-slate-300" 
            />
          </motion.button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <motion.div
        className="lg:hidden fixed inset-0 z-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: isMobileMenuOpen ? 1 : 0 }}
        transition={{ duration: 0.3 }}
        style={{ pointerEvents: isMobileMenuOpen ? 'auto' : 'none' }}
      >
        <div 
          className="absolute inset-0 bg-black/50 backdrop-blur-sm" 
          onClick={handleMobileMenuClose} 
        />
        <motion.div 
          className="absolute right-0 top-0 h-full w-80 bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-700"
          initial={{ x: '100%' }}
          animate={{ x: isMobileMenuOpen ? 0 : '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        >
          <div className="p-6 h-full flex flex-col">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">F</span>
                </div>
                <span className="text-xl font-bold text-slate-900 dark:text-white">Menu</span>
              </div>
              <motion.button
                onClick={handleMobileMenuClose}
                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <IconWrapper icon={HiXMark} className="h-6 w-6 text-slate-600 dark:text-slate-400" />
              </motion.button>
            </div>
            
            <nav className="flex-1 space-y-2">
              {navLinks.map((link, index) => (
                <motion.div 
                  key={link.name}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  {link.megaMenu ? (
                    <div className="space-y-2">
                      <button
                        onClick={() => setOpenMobileDropdown(openMobileDropdown === link.name ? null : link.name)}
                        className="w-full flex items-center justify-between py-3 px-3 font-semibold text-slate-900 dark:text-white rounded-lg bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all duration-200"
                      >
                        <span>{link.name}</span>
                        <IconWrapper 
                          icon={HiChevronDown} 
                          className={`w-4 h-4 transition-transform ${openMobileDropdown === link.name ? 'rotate-180' : ''}`} 
                        />
                      </button>
                      <motion.div
                        initial={false}
                        animate={{
                          height: openMobileDropdown === link.name ? 'auto' : 0,
                          opacity: openMobileDropdown === link.name ? 1 : 0
                        }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className="overflow-hidden mt-2 rounded-2xl bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 shadow-sm"
                      >
                        <div 
                          className="space-y-6 px-2 pb-4 max-h-[60vh] overflow-y-auto"
                          style={{
                            scrollbarWidth: 'thin',
                            scrollbarColor: '#cbd5e1 #f1f5f9'
                          }}
                        >
                          <style>{`
                            .dark .space-y-6::-webkit-scrollbar {
                              width: 6px;
                            }
                            .space-y-6::-webkit-scrollbar {
                              width: 6px;
                            }
                            .space-y-6::-webkit-scrollbar-track {
                              background: #f1f5f9;
                              border-radius: 10px;
                            }
                            .dark .space-y-6::-webkit-scrollbar-track {
                              background: #1e293b;
                            }
                            .space-y-6::-webkit-scrollbar-thumb {
                              background: #cbd5e1;
                              border-radius: 10px;
                            }
                            .dark .space-y-6::-webkit-scrollbar-thumb {
                              background: #475569;
                            }
                            .space-y-6::-webkit-scrollbar-thumb:hover {
                              background: #94a3b8;
                            }
                            .dark .space-y-6::-webkit-scrollbar-thumb:hover {
                              background: #64748b;
                            }
                          `}</style>
                          <div>
                            <div className="flex items-center justify-between mb-4">
                              <span className="text-sm font-semibold uppercase tracking-wide text-blue-600">{link.megaMenu?.services.title}</span>
                              <Link to={link.megaMenu?.services.viewAllPath || '/services'} className="text-sm font-medium text-blue-600" onClick={handleMobileMenuClose}>
                                View all
                              </Link>
                            </div>
                            {link.megaMenu?.services.categories.map(category => (
                              <div key={category.title} className="mb-4">
                                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">{category.title}</p>
                                <div className="grid grid-cols-1 gap-2">
                                  {category.items.map(item => (
                                    <NavLink
                                      key={item.name}
                                      to={item.path}
                                      className={({ isActive }) => `
                                        block py-2 px-3 text-sm rounded-lg transition-all duration-200
                                        ${isActive 
                                          ? 'text-blue-600 dark:text-blue-400 font-semibold bg-blue-50 dark:bg-slate-800' 
                                          : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'
                                        }
                                      `}
                                      onClick={handleMobileMenuClose}
                                    >
                                      {item.name}
                                    </NavLink>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                          <div>
                            <div className="flex items-center justify-between mb-4">
                              <span className="text-sm font-semibold uppercase tracking-wide text-purple-600">{link.megaMenu?.solutions.title}</span>
                              <Link to={link.megaMenu?.solutions.viewAllPath || '/solutions'} className="text-sm font-medium text-purple-600" onClick={handleMobileMenuClose}>
                                View all
                              </Link>
                            </div>
                            <div className="space-y-3">
                              {link.megaMenu?.solutions.cards.map(card => (
                                <NavLink
                                  key={card.name}
                                  to={card.path}
                                  className={({ isActive }) => `
                                    block rounded-2xl p-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-lg transition-all duration-300
                                    ${isActive ? 'ring-2 ring-blue-500' : ''}
                                  `}
                                  onClick={handleMobileMenuClose}
                                >
                                  <p className="text-sm font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">{card.name}</p>
                                  <p className="text-sm mt-1 leading-snug text-slate-900 dark:text-white">{card.description}</p>
                                </NavLink>
                              ))}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    </div>
                  ) : link.subLinks ? (
                    <div className="space-y-2">
                      <button
                        onClick={() => setOpenMobileDropdown(openMobileDropdown === link.name ? null : link.name)}
                        className="w-full flex items-center justify-between py-3 px-3 font-semibold text-slate-900 dark:text-white rounded-lg bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all duration-200"
                      >
                        <span>{link.name}</span>
                        <IconWrapper 
                          icon={HiChevronDown} 
                          className={`w-4 h-4 transition-transform ${openMobileDropdown === link.name ? 'rotate-180' : ''}`} 
                        />
                      </button>
                      <motion.div
                        initial={false}
                        animate={{
                          height: openMobileDropdown === link.name ? 'auto' : 0,
                          opacity: openMobileDropdown === link.name ? 1 : 0
                        }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className="overflow-hidden mt-2 rounded-2xl bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 shadow-sm"
                      >
                        <div className="pl-4 space-y-1 pb-2">
                          {link.subLinks.map((subLink, subIndex) => (
                            <motion.div
                              key={subLink.name}
                              initial={{ opacity: 0, x: 10 }}
                              animate={{ opacity: openMobileDropdown === link.name ? 1 : 0, x: 0 }}
                              transition={{ delay: openMobileDropdown === link.name ? (subIndex * 0.05) : 0 }}
                            >
                              <NavLink
                                to={subLink.path}
                                className={({ isActive }) => `
                                  block py-2 px-3 text-sm rounded-lg transition-all duration-200
                                  ${isActive 
                                    ? 'text-blue-600 dark:text-blue-400 font-semibold bg-blue-50 dark:bg-slate-800' 
                                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'
                                  }
                                `}
                                onClick={handleMobileMenuClose}
                              >
                                {subLink.name}
                              </NavLink>
                            </motion.div>
                          ))}
                        </div>
                      </motion.div>
                    </div>
                  ) : (
                    <NavLink
                      to={link.path}
                      className={({ isActive }) => `
                        block py-3 px-3 font-medium rounded-lg transition-all duration-200
                        ${isActive 
                          ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-slate-800' 
                          : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                        }
                      `}
                      onClick={handleMobileMenuClose}
                    >
                      {link.name}
                    </NavLink>
                  )}
                </motion.div>
              ))}
            </nav>
          </div>
        </motion.div>
      </motion.div>
    </motion.nav>
  );
};